<!-- TODO: Show view in the browser -->
<template>
  <div class="content">
    <a-spin
      :spinning="isTypificationTreeMutating || isLoading"
      class="content__wrapper"
      style="margin-bottom: -12px"
    >
      <div class="content__body">
        <a-button type="link" class="button" @click="$router.push('/operations/campaigns/')">
          <ArrowLeftOutlined />
          Volver a campañas
        </a-button>

        <div class="content__header">
          <h3 class="content__header__title">
            Tipificacion de campaña:
            <a-tooltip v-if="portfolioName.length > 8 && !isLarge">
              <template #title> {{ portfolioName }} </template>
              {{ portfolioName.substring(0, 4) + "..." }}
            </a-tooltip>
            <span v-else> {{ portfolioName }} </span>

            <a-tooltip>
              <template #title>
                Crea tu árbol de tipificaciones con los resultados que guardarás luego de cada
                llamada o interacción.
                <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer"> Click aquí </a>
                para conocer más.
              </template>

              <InfoCircleOutlined style="margin-left: 4px" />
            </a-tooltip>
          </h3>
          <div class="content__header__actions">
            <a-button type="link" :disabled="!isDisabled" @click="handleToggleVisibilityItems">
              <u>
                {{ hideDisabled ? "Mostrar" : "Ocultar" }} desactivados
                <EyeInvisibleOutlined v-if="!hideDisabled" />
                <EyeOutlined v-if="hideDisabled" />
              </u>
            </a-button>
            <TypificationGroup @push="handleAddNewGroup" />
          </div>
        </div>
        <div class="separator"></div>

        <div class="empty-typification" v-if="isTypificationEmpty">
          <img src="@/app/entities/assets/typifications.png" class="image" alt="Tipificaciones" />
          <span class="empty-typification__description">
            Para crear una tipificación, debes comenzar creando un grupo
          </span>
          <div class="center">
            <TypificationGroup @push="handleAddNewGroup" />
          </div>
        </div>

        <CustomScrollbar v-else height="calc(100% - 75px)" auto>
          <TypificationTree
            ref="tree"
            :key="index"
            :customizedManagement="record?.customized_management"
            :equivalencies="record?.equivalences_nc"
            @updateTree="handleFetchTypificationByPortfolio"
          />
        </CustomScrollbar>
      </div>
      <div class="content__footer" v-if="lastModified != ''">
        <div class="config__acw">
          <div class="text-last-modification">Última modificación {{ lastModified }}</div>
        </div>
      </div>
    </a-spin>

    <section class="config-section">
      <header class="header">
        <h4>Configuraciones</h4>
      </header>

      <p class="description">
        Realiza configuraciones para tus tipificaciones.
        <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer"> Click aquí </a>
        para conocer más.
      </p>

      <div
        class="card"
        id="no-contact-equivalencies"
        :class="{ warning__border: isNCEquivalenciesIncomplete }"
        @click="handleOpenNCEquivalenciesModal"
      >
        <h4>Equivalentes para el Grupo “No contacto” de una estrategia Predictiva</h4>

        <p>
          Si tu campaña realizará <strong>estrategias de tipo predictivo</strong>, es importante que
          configures los equivalentes para el Grupo “No contacto”.

          <a-tooltip v-if="isNCEquivalenciesIncomplete">
            <template #title>
              Se ha eliminado uno de los resultados configurados. Recuerda que, si lanzarás
              estrategias de tipo predictivo, todas las equivalencias son obligatorias.
            </template>

            <WarningOutlined class="warning__icon" />
          </a-tooltip>
        </p>
      </div>

      <div
        v-if="typificationTypeDefault == 2"
        class="card config__acw__default"
        id="default-typification"
        @click="handleShowModal"
      >
        <h4>Tipificación por defecto</h4>

        <p>
          <span class="icon-required">*</span>
          Según tu configuracion de ACW, debes seleccionar una tipificación por defecto
        </p>
      </div>
    </section>
  </div>

  <TypificationsModalDefault
    :visible="visible"
    v-if="visible"
    :record="record?.customized_management"
    @push="handleSaveTypificationDefault"
    @close="handleCloseModal"
  />

  <NoContactEquivalenciesModal
    v-if="NCEquivalenciesModalVisible"
    :visible="NCEquivalenciesModalVisible"
    :entity-name="record?.entity?.name"
    :equivalencies="record?.equivalences_nc"
    :isNCEquivalenciesIncomplete="isNCEquivalenciesIncomplete"
    @save="handleNCEquivalenciesSave"
    @close="handleCloseNCEquivalenciesModal"
  />
</template>

<script>
import { defineComponent, ref, provide } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { Modal, message } from "ant-design-vue";
import {
  InfoCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  WarningOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons-vue";

import { stringToDate } from "@/app/shared/utils/dates";
import { fetchPortfolioById, updatePortfolio } from "@/app/entities/services";

import TypificationGroup from "@/app/entities/components/drawers/typifications/TypificationGroup.vue";
import NoContactEquivalenciesModal from "@/app/entities/components/modals/typifications/NoContactEquivalenciesModal.vue";
import TypificationsModalDefault from "@/app/entities/components/modals/typifications/TypificationsModalDefault.vue";
import TypificationTree from "@/app/entities/components/picker/TypificationTree.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

export default defineComponent({
  components: {
    TypificationGroup,
    TypificationTree,
    InfoCircleOutlined,
    // eslint-disable-next-line vue/no-unused-components
    TypificationsModalDefault,
    NoContactEquivalenciesModal,
    EyeInvisibleOutlined,
    EyeOutlined,
    WarningOutlined,
    CustomScrollbar,
    ArrowLeftOutlined,
  },
  setup() {
    const index = ref(0);
    const isLoading = ref(false);

    const NCEquivalenciesModalVisible = ref(false);

    const handleOpenNCEquivalenciesModal = () => {
      NCEquivalenciesModalVisible.value = true;
    };

    const handleCloseNCEquivalenciesModal = () => {
      NCEquivalenciesModalVisible.value = false;
    };

    const record = ref(undefined);

    provide("currentStrategy", record);

    onBeforeRouteLeave(async () => {
      let isAllowToNavigate =
        record.value.type_default_management === 1 || record.value.customized_management;

      if (isAllowToNavigate) return true;

      function confirmQuit() {
        return new Promise((resolve, reject) => {
          Modal.confirm({
            title: "¿Seguro que quieres salir sin configurar la tipificación por defecto?",
            content:
              "Si sales sin guardar la tipificación por defecto, la campaña parasará a guardar logs por defecto",
            okText: "Salir",
            cancelText: "Quedarse",
            centered: true,
            onOk: async () => {
              try {
                await updatePortfolio(record.value.id, {
                  type_management: 1,
                  name: record.value.name,
                  entity: record.value.entity.id,
                });
                resolve(true);
              } catch (error) {
                reject(error);
              }
            },
            onCancel: async () => {
              console.log("CANCEL");
              resolve(false);
            },
          });
        });
      }

      try {
        isAllowToNavigate = await confirmQuit();
        if (isAllowToNavigate)
          message.warning(`La campaña ${record.value.name} ha pasado a guardar logs por defecto`);
      } catch (error) {
        console.error(error);
        message.error("Ha ocurrido un error al actualizar la campaña");
      }

      return isAllowToNavigate;
    });

    return {
      index,
      record,
      isLoading,
      NCEquivalenciesModalVisible,
      handleOpenNCEquivalenciesModal,
      handleCloseNCEquivalenciesModal,
    };
  },
  data() {
    const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/operaciones/campañas-1/`;

    return {
      loading: false,
      hideDisabled: false,
      windowWidth: window.innerWidth,
      visible: false,
      DOCS_URL,
    };
  },
  computed: {
    isDisabled() {
      return this.$store.getters["getTypificationDisabled"];
    },
    isLarge() {
      return this.windowWidth > 1500;
    },
    portfolioName() {
      return this.$store.getters["getTypificationPortfolioName"];
    },
    isTypificationEmpty() {
      const tree = this.$store.getters["getTypificationTree"];
      return !tree || tree.length < 1;
    },
    isTypificationTreeMutating() {
      return this.$store.getters["isTypificationTreeMutating"];
    },
    typificationTypeDefault() {
      return this.$store.getters["getTypificationDefault"];
    },
    lastModified() {
      const currentDate = new Date();
      const lastModified = this.$store.getters["getLastModification"];
      if (lastModified != "") {
        const modificate = stringToDate(lastModified).valueOf() - currentDate.valueOf();
        if (modificate <= 60000) {
          return "hace unos segundos";
        }
        if (modificate <= 3600000) {
          return "hace unos " + Math.round(modificate / 60000) + " minutos";
        }
        if (modificate <= 86400000) {
          return "a las " + lastModified.split(" ")[1];
        }
        if (modificate > 86400000 && modificate < 259200000) {
          return (
            "hace " +
            Math.round(modificate / 86400000) +
            " dias a las " +
            lastModified.split(" ")[1]
          );
        }
        return "el " + lastModified.split(" ")[0] + " a las " + lastModified.split(" ")[1];
      }

      return "";
    },
    isNCEquivalenciesIncomplete() {
      const equivalencies = this.record?.equivalences_nc;

      const isIncomplete = equivalencies?.some((equivalency) => equivalency.result === null);

      if (isIncomplete) {
        let nullQuantity = 0;

        equivalencies.forEach(({ result }) => {
          if (result === null) nullQuantity++;
        });

        if (nullQuantity !== equivalencies.length) return true;
      }

      return false;
    },
  },
  mounted() {
    this.handleFetchTypificationByPortfolio();
    this.handleFetchPortfolio();
    this.$store.dispatch("getFormsTypifications");
    this.$store.commit("RESET_STATE");
    window.addEventListener("resize", () => {
      this.windowWidth = window.innerWidth;
    });
  },
  unmounted() {
    window.removeEventListener("resize", () => {
      console.log(" remove event ");
    });
  },
  methods: {
    handleToggleVisibilityItems() {
      this.hideDisabled = !this.hideDisabled;
      this.$refs.tree.hideDisabled(this.hideDisabled);
      // TODO: SERGIO
    },
    async handleAddNewGroup(item) {
      try {
        await this.$store.dispatch("addItemToTypificationTree", {
          ...item,
          portfolio: this.$route.params.id,
        });
        this.$message.success(`Se ha creado un nuevo grupo "${item.name}"`);
        this.handleFetchTypificationByPortfolio();
      } catch (error) {
        const errorMessage = error?.getCleanErrorMessage() || `Ha ocurrido un error`;

        this.$message.error(errorMessage);
      }
    },
    async handleFetchPortfolio(portfolioId = this.$route.params.id ?? this.record?.id) {
      // Falls back to the loaded portfolio because `params.id` is gone when the user
      // leaves the campaign view before the post-save refresh runs.
      if (!portfolioId) return;

      const response = await fetchPortfolioById(portfolioId);
      this.record = response.data;
    },
    async handleFetchTypificationByPortfolio() {
      try {
        this.isLoading = true;
        const id = this.$route.params.id;
        await this.$store.dispatch("fetchTypificationByPortfolio", id);
        this.index++;
        this.$store.commit("SET_TYPIFICATION_DISABLED", false);
        await this.handleFetchPortfolio();
        this.isLoading = false;
      } catch {
        this.$router.push("/operations/campaigns");
      }
    },
    async handleSaveTypificationDefault(data) {
      const portfolioId = this.$route.params.id ?? this.record?.id;

      try {
        const body = {
          entity: this.record.entity.id,
          name: this.record.name,
          customized_management: data,
        };
        await this.$store.dispatch("updatePortfolioById", {
          portfolioId,
          portfolio: body,
        });
        this.$notification.success({
          message: `Tipificacion por defecto`,
          description: "La tipificacion se guardó correctamente",
        });
      } catch(error) {
        console.log(error, 'error');
        const errorMessage = error?.getErrorMessage() || `Ha ocurrido un error`;

        this.$message.error(errorMessage);
        return;
      }

      // The save already succeeded: a failed refresh is not a save error.
      try {
        await this.handleFetchPortfolio(portfolioId);
      } catch (error) {
        console.error("[TypificationsTemplate] portfolio refresh failed", error);
      }
    },
    handleShowModal() {
      this.visible = true;
    },
    handleCloseModal() {
      this.visible = false;
    },
    async handleNCEquivalenciesSave() {
      await this.handleFetchPortfolio();
    },
  },
});
</script>

<style lang="sass" scoped>
.button
  padding: 0
  height: 22px

.empty-typification
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  height: 85%

  &__description
    color: $gray-8

.image
  display: block
  margin-bottom: 24px

.center
  display: flex
  justify-content: center

.content
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 24px
  &__wrapper
    height: 100%

  &__title
    margin-left: 20px
    margin-top: 10px
    margin-bottom: 0px

  &__body
    flex: auto
    padding-left: 20px
    padding-top: 12px
    height: calc(100vh - #{$header-height} - 24px - 120px)
    // height: calc(37vw)

  &__header
    display: flex
    justify-content: space-between
    align-items: center
    padding: 12px 0
    border-bottom: $border-normal

    &__title
      font-weight: 600
      margin-bottom: 0
    &__actions
      display: flex

  &__footer
    padding: 10px
    background-color: white
    z-index: 1

.separator
  margin-top: 6px

.popover__paragraph
  width: 230px

.config__acw
  margin-left: 20px
  border-top: $border-normal
  display: flex
  margin-left: 20px
  padding-top: 10px

  &__default
    // margin-top: -20px

.config-section
  padding-right: 20px

  .header
    padding: 12px 0 5px
    display: flex
    align-items: center

    h4
      margin: 0
      color: $gray-9

  .description
    color: $gray-8

  .card
    color: $gray-8
    width: 100%
    margin-bottom: 20px
    border-radius: 8px
    border: 1px solid transparent
    padding: 16px
    gap: 80px
    box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.1)

    &:hover
      border: 1px solid $blue-6
      cursor: pointer

    p
      margin: 0

    h4
      margin-bottom: 8px
      font-size: 16px
      font-weight: 600
      color: $gray-8

.text-last-modification
  color: $gray-8
  font-size: 12px
  font-style: italic
  font-weight: 300
  line-height: 40px
  letter-spacing: 0em
  text-align: left
  margin: 0 10px
.icon-required
  margin-right: 4px
  color: #ff4d4f
  font-size: 14px
  font-family: SimSun, sans-serif
  line-height: 1

.button--add
  padding: 0

.warning
  &__icon
    color: $orange-6
    margin-left: 2px

  &__border
    border-color: $orange-6 !important

:deep(.ant-spin-container)
  height: 100%
  display: grid
</style>
